import { httpClient } from './api/httpClient';
import { isMockEnabled, mockResponse } from './api/mockAdapter';

/**
 * Factory producing a CRUD service that transparently switches between mock
 * mode and the real API. In mock mode it keeps an in-memory working copy so
 * create/update/delete feel real during a session.
 *
 * list() always resolves to { items, total, page, pageSize, totalPages }.
 *
 * @param endpoints  { LIST, CREATE, DETAIL(id), UPDATE(id), DELETE(id) }
 * @param toModel    (raw) => uiModel       (snake_case -> camelCase)
 * @param toPayload  (model) => raw         (camelCase -> snake_case)
 * @param mockSeed   array of raw records
 * @param idPrefix   prefix for generated mock ids
 */
export const createCrudService = ({ endpoints, toModel, toPayload, mockSeed = [], idPrefix = 'id' }) => {
  let store = mockSeed.map((r) => ({ ...r }));

  return {
    async list(params = {}) {
      if (isMockEnabled()) {
        const raw = await mockResponse(store);
        const items = raw.map(toModel);
        return { items, total: items.length, page: 1, pageSize: items.length || 20, totalPages: 1 };
      }
      const data = await httpClient.get(endpoints.LIST, { params });
      // Backend always returns PaginatedResponse envelope.
      const items = (data.items ?? (Array.isArray(data) ? data : [])).map(toModel);
      return {
        items,
        total: data.total ?? items.length,
        page: data.page ?? 1,
        pageSize: data.page_size ?? params.page_size ?? 20,
        totalPages: data.total_pages ?? 1,
      };
    },

    async getById(id) {
      if (isMockEnabled()) return toModel(await mockResponse(store.find((r) => r.id === id)));
      return toModel(await httpClient.get(endpoints.DETAIL(id)));
    },

    async create(model) {
      if (isMockEnabled()) {
        const created = {
          ...toPayload(model),
          id: `${idPrefix}-${Date.now()}`,
          created_at: new Date().toISOString(),
        };
        store = [created, ...store];
        return toModel(await mockResponse(created));
      }
      return toModel(await httpClient.post(endpoints.CREATE, toPayload(model)));
    },

    async update(id, model) {
      if (isMockEnabled()) {
        store = store.map((r) => (r.id === id ? { ...r, ...toPayload(model), id } : r));
        return toModel(await mockResponse(store.find((r) => r.id === id)));
      }
      return toModel(await httpClient.put(endpoints.UPDATE(id), toPayload(model)));
    },

    async remove(id) {
      if (isMockEnabled()) {
        store = store.filter((r) => r.id !== id);
        await mockResponse({ success: true });
        return id;
      }
      await httpClient.delete(endpoints.DELETE(id));
      return id;
    },

    /** Escape hatch for custom operations that still need the store/transforms. */
    _getStore: () => store,
    _setStore: (next) => { store = next; },
    toModel,
    toPayload,
  };
};

export default createCrudService;
